import { useAuth0 } from '@auth0/auth0-react';
import { SessionExpiredDialog, SessionExpiredError, needsReLogin } from '@iqd/shared-app';
import { HostAuthProvider } from '../shared/ui';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { environment } from '../../environments/environment';
import { PAGE_WIDTH } from '../layout/page-width';

const ROLES_CLAIM = environment.auth0.claimsNamespace + 'roles';
// Named `company`, but it holds the distributor's uuid rather than its id.
const COMPANY_CLAIM = environment.auth0.claimsNamespace + 'company';

/**
 * Fills the shared auth context.
 *
 * The shared screens read who is signed in from a context rather than from
 * Auth0 directly — it used to come down from the Angular host in one of the
 * two products, and the screens should not have to know which. There is no
 * host in either now, so both fill it from Auth0 themselves.
 */
/**
 * The frame, drawn while Auth0 works out who this is.
 *
 * `isLoading` covers a silent-auth round trip through an iframe, which is not
 * quick, and returning `null` for it left the window blank the whole time.
 *
 * The real menu bar cannot be drawn yet — it reads the signed-in user and the
 * Apollo cache, and neither exists above the providers this sits outside of —
 * so this is its markup with the labels greyed out. The three groups and their
 * class lists are `MenuBar`'s and `App`'s: a nav item is 32px (`text-sm` gives
 * it a 20px line box, `py-1.5` another 12px) and the bordered buttons 34px, so
 * nothing moves when the real bar replaces this one.
 */
function AppShellSkeleton() {
  return (
    <div className="min-h-screen bg-white text-hilevel-slate" role="status" aria-busy="true">
      <span className="sr-only">Signing in</span>

      <nav className="border-0 border-b border-solid border-hilevel-border bg-hilevel-offwhite">
        <div className={`${PAGE_WIDTH} flex items-center gap-3 pl-4 pr-4 py-2`}>
          {/* Below `lg` the bar collapses to this one button. */}
          <div className="h-[34px] w-[38px] animate-pulse rounded border border-hilevel-border bg-hilevel-light lg:hidden" />

          {/* Home and Quote. */}
          <div className="hidden flex-1 gap-1 lg:flex">
            <div className="h-8 w-[62px] animate-pulse rounded bg-hilevel-light" />
            <div className="h-8 w-[68px] animate-pulse rounded bg-hilevel-light" />
          </div>

          {/* Sign-in and support, which the real bar pushes to the right. */}
          <div className="ml-auto flex items-center gap-2">
            <div className="h-[34px] w-[72px] animate-pulse rounded bg-hilevel-light" />
            <div className="h-[34px] w-[68px] animate-pulse rounded bg-hilevel-light" />
          </div>
        </div>
      </nav>

      <main className="pb-8">
        <div className="m-8 h-40 animate-pulse rounded-lg bg-hilevel-light" />
      </main>
    </div>
  );
}

export function Auth0Bridge({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } =
    useAuth0();
  const [sessionExpired, setSessionExpired] = useState(false);
  // Once someone has said "not now", stop asking. Every query on the screen
  // fails the same way and there can be several of them, so reopening on each
  // would leave them unable to read the page they are being signed out of.
  const dismissed = useRef(false);

  /**
   * The token every screen asks for, with a signed-out session answered here
   * rather than in each screen that renders `error.message`.
   *
   * The rejection still happens — a query that cannot authenticate has not
   * succeeded — but it carries a sentence a person can act on, and the dialog
   * beside it is what offers the way back.
   */
  const getAccessToken = useCallback(async () => {
    try {
      return await getAccessTokenSilently();
    } catch (error) {
      if (!needsReLogin(error)) throw error;
      if (!dismissed.current) setSessionExpired(true);
      throw new SessionExpiredError();
    }
  }, [getAccessTokenSilently]);

  if (isLoading) {
    return <AppShellSkeleton />;
  }

  return (
    <HostAuthProvider
      auth={{
        user: user
          ? {
              sub: user.sub,
              name: user.name,
              email: user.email,
              picture: user.picture,
              companyUuid: user[COMPANY_CLAIM] as string | undefined,
              roles: (user[ROLES_CLAIM] as string[]) ?? [],
            }
          : null,
        isAuthenticated,
        getAccessToken,
      }}
    >
      {children}
      <SessionExpiredDialog
        open={sessionExpired}
        onClose={() => {
          dismissed.current = true;
          setSessionExpired(false);
        }}
        // Where to come back to. `loginWithRedirect` leaves the document, so
        // this is the only record of which screen the session died on.
        onSignIn={() =>
          loginWithRedirect({
            appState: { returnTo: `${window.location.pathname}${window.location.search}` },
          })
        }
      />
    </HostAuthProvider>
  );
}
